import { useState, useEffect, useRef } from 'react'
import { User as UserIcon } from 'lucide-react'
import { useImageCache } from '../hooks/useImageCache'

interface ProfilePhotoProps {
  photoUrl?: string | null
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  showBorder?: boolean
}

const sizeClasses = {
  xs: 'w-5 h-5',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-16 h-16',
  xl: 'w-20 h-20'
}

const iconSizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-8 w-8',
  xl: 'h-10 w-10'
}

const ProfilePhoto = ({ photoUrl, size = 'md', className = '', showBorder = true }: ProfilePhotoProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const currentKeyRef = useRef<string | null>(null)
  const { getCachedUrl, loadImageUrl } = useImageCache()
  const getCachedUrlRef = useRef(getCachedUrl)
  const loadImageUrlRef = useRef(loadImageUrl)

  // Mantener referencias estables
  useEffect(() => {
    getCachedUrlRef.current = getCachedUrl
    loadImageUrlRef.current = loadImageUrl
  }, [getCachedUrl, loadImageUrl])

  useEffect(() => {
    // Resetear estado si cambia el photoUrl
    if (currentKeyRef.current !== photoUrl) {
      setSignedUrl(null)
      currentKeyRef.current = photoUrl || null
    }

    if (photoUrl && photoUrl.trim() !== '') {
      loadSignedUrl(photoUrl)
    } else {
      setSignedUrl(null)
      setLoading(false)
    }
  }, [photoUrl])

  const loadSignedUrl = async (key: string) => {
    try {
      setLoading(true)
      
      // Validar que la key no esté vacía
      if (!key || key.trim() === '') {
        setSignedUrl(null)
        setLoading(false)
        return
      }

      // Primero verificar el caché
      const cachedUrl = getCachedUrlRef.current(key)
      if (cachedUrl) {
        setSignedUrl(cachedUrl)
        setLoading(false)
        return
      }

      // Si no está en caché, cargarla
      const url = await loadImageUrlRef.current(key)
      if (url) {
        setSignedUrl(url)
      } else {
        setSignedUrl(null)
      }
    } catch (error) {
      console.error('Error loading photo signed URL:', error)
      setSignedUrl(null)
    } finally {
      setLoading(false)
    }
  }

  const sizeClass = sizeClasses[size]
  const iconSizeClass = iconSizeClasses[size]
  const borderClass = showBorder ? 'ring-2 ring-white shadow-sm' : ''

  return (
    <div className={`${sizeClass} rounded-full overflow-hidden bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center ${borderClass} ${className}`}>
      {signedUrl && !loading ? (
        <img
          src={signedUrl}
          alt="Foto de perfil"
          className="w-full h-full object-cover"
        />
      ) : (
        <UserIcon className={`${iconSizeClass} text-white`} />
      )}
    </div>
  )
}

export default ProfilePhoto

